// Import WordPress libraries
import { registerBlockVariation } from '@wordpress/blocks';
import { addFilter } from '@wordpress/hooks';
import { InspectorControls } from '@wordpress/block-editor';
import { PanelBody, ToggleControl, TextControl, RadioControl, RangeControl, SelectControl, FormTokenField,  BaseControl } from '@wordpress/components';
import { useState } from '@wordpress/element';
import { useSelect } from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';

// Import and set Embedded Posts variables
import EPIcon from './icon';
const EP = 'cooperativeit/embedded-posts';

// Registering Block Variation for Embedded Posts
registerBlockVariation('core/query', {
    name: EP,
    title: 'Embedded Posts',
    description: 'This plugin displays related posts via category, tags, author or title using either a Gutenberg block or a WordPress shortcode.',
    isActive: ['namespace'],
    icon: EPIcon,
    attributes: {
        namespace: EP,
        query: {
            postType: 'post',
            perPage: 5,
            offset: 0,
            relatedByAuthor: false,
            relatedByCategory: false,
            relatedByTag: true,
            searchByTitle: '',
            searchByPostID: '',
            relatedByOperator: 'AND',
            order: 'asc',
            orderBy: 'date',
            relatedByRandom: false,
            additionalPostTypes: [],
        },
    },
    allowedControls: [],
    innerBlocks: [
        [
            'core/post-template',
            {},
            [['core/post-title'], ['core/post-excerpt']],
        ],
        ['core/query-pagination'],
        ['core/query-no-results'],
    ],
});

// Checks if Embedded Posts is active or not
const isEmbeddedPosts = (props) => {
    const {
        attributes: { namespace },
    } = props;
    return namespace && namespace === EP;
};

// Embedded Posts Settings Panel
const EmbeddedPostsSettings = ({ props }) => {
    const [ ,setSelectedContinents ] = useState( [] );

    const postTypes = useSelect((select) =>
        select(coreStore)
            .getPostTypes()
            ?.filter(({ viewable }) => viewable)
            ?.map(({ slug }) => slug)
    );

    const currentPostType = props.attributes.query['postType'];
    const additionalPostTypes = props.attributes.query['additionalPostTypes'];

    // If the Additional Post Types is empty set it to the current post type
    if (additionalPostTypes.length === 0){
        additionalPostTypes.push(currentPostType);
    }

    const { query } = props.attributes;

    return (
        <PanelBody title="Embedded Posts Settings" initialOpen={!props.attributes.query['searchByPostID'] && !props.attributes.query['searchByTitle']}>
            <BaseControl
			    help={ 
			    	'WordPress contains different types of content and they are divided into collections called “Post types”. You have the flexibility to modify the post type for searching or add additional post types, enabling simultaneous searching across multiple post types.'
			     }>

                <FormTokenField
                    label="Post Types"
                    value={additionalPostTypes}
                    suggestions={postTypes?.filter((type) => type !== additionalPostTypes && type !== 'attachment')}
                    onChange={(tokens) => {
                        setSelectedContinents(tokens);

                        props.setAttributes({
                            query: {
                                ...props.attributes.query,
                                'additionalPostTypes': tokens
                            },
                            reloadTrigger: Date.now() // Update reloadTrigger here
                        });
                    }}
                    __experimentalExpandOnFocus
                />

            </BaseControl>

            {!query.relatedByRandom && 
            <SelectControl
                label="Order By"
                value={props.attributes.query['orderBy'] + '/' + props.attributes.query['order']}
                options={[
                    { label: 'Newest to oldest', value: 'date/desc' },
                    { label: 'Oldest to newest', value: 'date/asc' },
                    { label: 'A → Z', value: 'title/asc' },
                    { label: 'Z → A', value: 'title/desc' },
                ]}
                onChange={(value) => {

                    // Split the value using '/'
                    const [orderBy, order] = value.split('/');

                    // Update the attributes
                    props.setAttributes({
                        query: {
                            ...props.attributes.query,
                            'orderBy': orderBy,
                            'order': order,
                        },
                    });
                }}
            />}

            <ToggleControl
                label="Random Ordering"
                checked={ props.attributes.query['relatedByRandom'] }
                help='This will display a random ordering of the posts.'
                onChange={(random) => {
                
                    // Prepare the new attributes based on the condition
                    const newAttributes = {
                        ...props.attributes.query,
                        'relatedByRandom': random
                    };
                
                    // If random is false, add or update other attributes to trigger a re-render
                    if (!random) {
                        newAttributes.reloadTrigger = Date.now(); // Use a unique value to trigger re-render
                    }
                
                    // Set the attributes
                    props.setAttributes({
                        query: newAttributes,
                    });
                }}
                
                
            />
            <RangeControl
                label="Items Per Page"
                value={ props.attributes.query['perPage'] }
                onChange={(perpage) => {
                    props.setAttributes({
                        query: {
                            ...props.attributes.query,
                            'perPage': perpage,
                        },
                    });
                }}
                min={ 1 }
                max={ 10 }
            />
        </PanelBody>
    );
};

// Relate By Settings Panel
const RelatedBy = ({ props }) => {
    return (
        <PanelBody title="Related By" initialOpen={!props.attributes.query['searchByPostID'] && !props.attributes.query['searchByTitle']}>
            <ToggleControl
                label="Categories"
                checked={ props.attributes.query['relatedByCategory'] }
                onChange={ (category) => {
                    props.setAttributes({
                        query: {
                            ...props.attributes.query,
                            'relatedByCategory': category,
                        },
                    });
                } }
            />
            <ToggleControl
                label="Tags"
                checked={ props.attributes.query['relatedByTag'] }
                onChange={ (tag) => {
                    props.setAttributes({
                        query: {
                            ...props.attributes.query,
                            'relatedByTag': tag,
                        },
                    });
                } }
            />
            {
                props.attributes.query['relatedByCategory'] &&
                props.attributes.query['relatedByTag'] && (
                        <RadioControl
                            label="Taxonomy Operator"
                            help={
                                <div>
                                    AND will select posts related by both Tags and Categories.
                                    <br />
                                    OR will select posts related by either Tags and Categories.
                                </div>
                            }
                            selected={props.attributes.query['relatedByOperator']}
                            options={[
                                { label: 'AND', value: 'AND' },
                                { label: 'OR', value: 'OR' },
                            ]}
                            onChange={(operator) => {
                                props.setAttributes({
                                    query: {
                                        ...props.attributes.query,
                                        'relatedByOperator': operator,
                                    },
                                });
                            }}
                        />
                )
            }
            <ToggleControl
                label="Author"
                checked={ props.attributes.query['relatedByAuthor'] }
                onChange={ (author) => {
                    props.setAttributes({
                        query: {
                            ...props.attributes.query,
                            'relatedByAuthor': author,
                        },
                    });
                } }
            />
        </PanelBody>
    );
};

// Single Posts Settings Panel
const SinglePost = ({ props }) => {
    return (
        <PanelBody title="Single Post" initialOpen={!!props.attributes.query['searchByPostID'] || !!props.attributes.query['searchByTitle']}>
            <TextControl
                label=" Title"
                value={ props.attributes.query['searchByTitle'] }
                onChange={(searchTitle) => {
                    props.setAttributes({
                        query: {
                            ...props.attributes.query,
                            'searchByTitle': searchTitle,
                        },
                    });
                }}
            />
            <TextControl
                label=" Post ID"
                value={ props.attributes.query['searchByPostID'] }
                onChange={(searchID) => {
                    props.setAttributes({
                        query: {
                            ...props.attributes.query,
                            'searchByPostID': searchID,
                        },
                    });
                }}
            />
        </PanelBody>
    );
};

// Enable all setting panels
export const embeddedPostsControls = (BlockEdit) => (props) => {

    return isEmbeddedPosts(props) ? (
        <>
            <BlockEdit {...props} />
            <InspectorControls>
                <EmbeddedPostsSettings props={props} />
                <RelatedBy props={props} />
                <SinglePost props={props} />
            </InspectorControls>
        </>
    ) : (
        <BlockEdit {...props} />
    );
};

// Adding Filter for Block Edit to Include Custom Controls
addFilter('editor.BlockEdit', 'core/query', embeddedPostsControls);
